import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as IconLeftChevron } from "../assets/icon-left-chevron.svg";
import { ReactComponent as IconRightChevron } from "../assets/icon-right-chevron.svg";
import BigHeader from "../components/Elements/BigHeader";
import PaymentModal from "../components/Elements/PaymentModal";
import PrimaryButton from "../components/Elements/PrimaryButton";
import Spinner from "../components/Elements/Spinner";
import * as Constants from "../features/generalConstants";
import {
  addVat,
  formatCurrency,
  formatSizes,
  getUnitSizes,
  locationCompanyProfile,
  monthlyToWeekly,
} from "../features/helpers";
import Insurance from "../features/rapidstor/entities/Insurance";
import { LineItem } from "../features/rapidstor/entities/LineItem";
import Location from "../features/rapidstor/entities/Location";
import {
  useGetChargesQuery,
  useGetInsuranceCoverageQuery,
  useGetMerchandiseQuery,
  useGetMoveInCostRetrieveQuery,
  useGetReservationQuery,
  useGetUnitListQuery,
  usePostMoveInMutation,
  usePatchReservationMutation,
  usePostReservationMutation,
  usePostScaMoveInMutation,
  usePostScaMoveInPaymentMutation,
  usePostTrackEventMutation,
} from "../features/rapidstor/rapidstorApiSlice";
import {
  RapidStorMoveInRequest,
  RapidStorPaymentResult,
  RapidStorReservationRequest,
  RapidStorScaMoveInRequest,
  RapidStorTrackEvent,
  RapidStorTrackEventRequest,
  ReservationPatchRequest,
  options,
} from "../services/rapidstor";
import store from "../store";
import {
  selectContactData,
  selectReservationData,
  selectReservationToken,
  selectUnitData,
  setReservationData,
  setReservationToken,
  setUnitData,
  setExtras,
} from "../store/bookingSlice";
import classes from "./BookConfirm.module.scss";
import Descriptor from "../features/rapidstor/entities/Descriptor";
import { useCookies } from "react-cookie";
import UnitType from "../features/rapidstor/entities/UnitType";
import { createId } from "@paralleldrive/cuid2";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import moment from "moment";
import { Logger } from "../main";

export interface Fact {
  icon: JSX.Element;
  text: string;
}

export interface BookingForm {
  moveInDate: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address_1: string;
  address_2: string;
  town: string;
  county: string;
  postcode: string;
  insurance: "yes" | "self" | "student";
  valueOfGoods: number;
  autoBill: boolean;
  termsAndConditions: boolean;
  allowMarketing: boolean;
  cardType: string;
  cardName: string;
  cardNumber: number;
  cardExpMonth: number;
  cardExpYear: number;
  cardCVV: number;
}

export type SubmitState =
  | "init"
  | "prepareMoveIn"
  | "moveIn"
  | "prepareBillingInfoUpdate"
  | "billingInfoUpdate"
  | "preparePayment"
  | "payment"
  | "complete"
  | "partialComplete"
  | "error";

function BookConfirm() {
  /**
   * GENERAL PAGE STATE
   */
  const location = useLoaderData() as Location;
  const navigate = useNavigate();
  const unitData = useSelector(selectUnitData);
  const reservationData = useSelector(selectReservationData);
  const reservationToken = useSelector(selectReservationToken);
  const contactData = useSelector(selectContactData);
  const [cookies, removeCookie] = useCookies(["user"]);
  const { reservationTokenParams } = useParams();
  const CREATE_LEAD_RETRY_LIMIT = 2;

  /**
   * UI CONTROLLING STATE
   */
  const [neededDateError, setNeededDateError] = useState<boolean>(true);
  const [valueOfGoodsError, setValueOfGoodsError] = useState<string | null>(null);
  const [cardCVVError, setCardCCVError] = useState<boolean>(true);
  const [submissionState, setSubmissionState] = useState<SubmitState>("init");
  const [moveInError, setMoveInError] = useState<string>("");
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [loadingForm, setLoadingForm] = useState<boolean>(true);
  const [formClass, setFormClass] = useState<string>(classes.form);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [availableInsurance, setAvailableInsurance] = useState<Insurance>();
  const [sizes, setSizes] = useState<(string | number)[]>([]);
  const [createLeadRetryCount, setCreateLeadRetryCount] = useState<number>(0);

  /**
   * QUERIES
   */
  const [
    makeReservation,
    {
      isLoading: createReservationLoading,
      isSuccess: createReservationSuccess,
      error: createReservationError,
    },
  ] = usePostReservationMutation();
  const [skipFetchMoveInCostRetrieve, setSkipMoveInCostRetrieve] =
    useState<boolean>(true);
  const [doFetchData, setDoFetchData] = useState<boolean>(true);
  const [makeMoveIn] = usePostMoveInMutation();
  const [makeScaMoveIn] = usePostScaMoveInMutation();
  const [makeScaMoveInPayment] = usePostScaMoveInPaymentMutation();
  const [updateReservation] = usePatchReservationMutation();
  const [postTrackEvent] = usePostTrackEventMutation();

  const {
    data: reservation,
    isError: reservationError,
    isSuccess: reservationSuccess,
  } = useGetReservationQuery(
    {
      locationCode: location.locationCode,
      token: reservationTokenParams,
    },
    {
      skip: doFetchData,
    }
  );
  const { data: unitList } = useGetUnitListQuery(location.locationCode, {
    skip: doFetchData,
  });
  const { data: merchandise, isFetching: fetchingMerchandise } = useGetMerchandiseQuery(
    location.locationCode
  );
  const { data: charges, isFetching: fetchingCharges } = useGetChargesQuery(
    location.locationCode
  );
  const { data: insurance, isFetching: fetchingInsurance } = useGetInsuranceCoverageQuery(
    location.locationCode
  );
  const { data: costRetrieve, isFetching: fetchingCostRetrieve } =
    useGetMoveInCostRetrieveQuery(
      {
        location: location,
        reservationId: reservationToken || reservationTokenParams,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: skipFetchMoveInCostRetrieve,
      }
    );

  const [form, updateForm] = useState<BookingForm>({
    moveInDate: (reservationData?.payload.neededDate as string) ?? "",
    firstName: reservationData?.payload.tenant.firstName as string,
    lastName: reservationData?.payload.tenant.lastName as string,
    phone: reservationData?.payload.tenant.phone as string,
    email: reservationData?.payload.tenant.email as string,
    address_1: reservationData?.payload.tenant.addr1 as string,
    address_2: reservationData?.payload.tenant.addr2 as string,
    town: reservationData?.payload.tenant.city as string,
    county: reservationData?.payload.tenant.state as string,
    postcode: reservationData?.payload.tenant.postalCode as string,
    insurance: "yes",
    valueOfGoods: reservationData?.payload.tenant.declaredInsuranceAmt as number,
    autoBill: true,
    termsAndConditions: false,
    allowMarketing: true,
    // PAYMENT DETAILS
    cardType: "5",
    cardName: "",
    cardNumber: 0,
    cardExpMonth: 0,
    cardExpYear: 0,
    cardCVV: 0,
  });

  useEffect(() => {
    if (!contactData || !unitData) {
      if (!reservationTokenParams) {
        if (cookies.user) {
          removeCookie("user", { url: "/" });
        }
        navigate(`/${location.slug}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitData, contactData]);

  useEffect(() => {
    if (sizes.length === 0 && skipFetchMoveInCostRetrieve && reservationTokenParams) {
      setDoFetchData(false);
      if (reservationSuccess) {
        console.log("Populating state", reservationData);
        Logger().log(`Populating state: ${JSON.stringify(reservationData)}`);
        populateState();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitList, sizes, skipFetchMoveInCostRetrieve, reservationSuccess]);

  useEffect(() => {
    if (
      !createReservationLoading &&
      !reservationToken &&
      !createReservationSuccess &&
      !createReservationError
    ) {
      createLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createReservationLoading,
    createReservationSuccess,
    createReservationError,
    reservationToken,
  ]);

  useEffect(() => {
    if (!fetchingInsurance && !fetchingMerchandise && !fetchingCharges && costRetrieve) {
      setupLineItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchingInsurance,
    fetchingMerchandise,
    fetchingCharges,
    costRetrieve,
    fetchingCostRetrieve,
  ]);

  useEffect(() => {
    if (!costRetrieve || fetchingCostRetrieve) {
      setLoadingForm(false);
    } else {
      setLoadingForm(true);
    }
  }, [fetchingCostRetrieve, costRetrieve]);

  useEffect(() => {
    if (reservationToken && skipFetchMoveInCostRetrieve) {
      patchReservation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedUnitType = useMemo(() => {
    if (!unitData) {
      return;
    }
    // Get the cheapest available unit type.
    return unitData.selectedDescriptor.unitTypes.reduce(
      (selected: UnitType | null, uT) => {
        if (!uT.hasComputedAvailable) {
          return selected;
        }
        if (!selected || uT.stdWeeklyRate < selected.stdWeeklyRate) {
          return uT;
        }
        return selected;
      },
      null
    );
  }, [unitData]);

  const populateState = () => {
    if (reservation === null || reservationError) {
      if (cookies.user) {
        removeCookie("user", { url: "/" });
      }
      navigate(`/${location.slug}`);
    }
    if (!reservationToken && reservationTokenParams) {
      setReservationToken(reservationTokenParams);
    }
    if (unitList && reservation && sizes.length === 0) {
      const formattedSizes = formatSizes(unitList.descriptors);
      setSizes(getUnitSizes(formattedSizes.sortedList));
      const descriptors = unitList?.descriptors as Descriptor[];
      const unitSizes = formattedSizes.formattedSizes;
      const selectedDescriptor =
        descriptors[formattedSizes.formattedSizes.indexOf(reservation.unitType.area)];
      const unitType = descriptors[
        formattedSizes.formattedSizes.indexOf(reservation.unitType.area)
      ].unitTypes.filter((uT) => {
        if (uT._id === reservation.unitType._id) {
          return uT;
        }
      });
      const data = {
        location,
        unitType: unitType[0],
        unitSizes,
        descriptors,
        selectedDescriptor,
        reservation: false,
      };
      store.dispatch(setUnitData(data));

      let neededDate = moment(reservationData?.payload.neededDate);
      if (neededDate.isBefore(moment())) {
        neededDate = moment().endOf("day");
      }

      const reservationPayload: RapidStorReservationRequest = {
        locationCode: location.locationCode,
        payload: {
          customListing: true,
          enquiry: false,
          neededDate: neededDate.toISOString(),
          timezoneOffset: location.timezoneOffset,
          sourceUrl: window.location.href,
          tenant: {
            tenantId: null,
            firstName: reservation.firstName,
            lastName: reservation.lastName,
            email: reservation.email,
            phone: reservation.phone,
            addr1: reservation.addr1,
            addr2: reservation.addr2,
            city: reservation.city,
            state: reservation.state,
            postalCode: reservation.postalCode,
            declaredInsuranceAmt: reservation.declaredInsuranceAmt,
          },
          unitType: reservation.unitType._id,
          deal: reservation.deal._id,
          token: reservation._id,
        },
      };
      store.dispatch(setReservationData(reservationPayload));
      updateForm({
        moveInDate: neededDate.toISOString(),
        firstName: reservation.firstName as string,
        lastName: reservation.lastName as string,
        phone: reservation.phone as string,
        email: reservation.email as string,
        address_1: reservation.addr1 as string,
        address_2: reservation.addr2 as string,
        town: reservation.city as string,
        county: reservation.state as string,
        postcode: reservation.postalCode as string,
        insurance: "yes",
        valueOfGoods: reservation.declaredInsuranceAmt as number,
        autoBill: true,
        termsAndConditions: false,
        allowMarketing: true,
        // PAYMENT DETAILS
        cardType: "5",
        cardName: "",
        cardNumber: 0,
        cardExpMonth: 0,
        cardExpYear: 0,
        cardCVV: 0,
      });
      setSkipMoveInCostRetrieve(false);
    }
  };

  const findInsurance = useCallback(
    (value = 0) => {
      const area = unitData?.selectedDescriptor.area;
      if (!insurance || !unitData || area === undefined) {
        return;
      }
      if (typeof area !== "number") {
        Logger().error(`Area is not a number: ${unitData?.selectedDescriptor}`);
        return;
      }
      const insurances = [...insurance.insuranceTypes].sort(
        (a, b) => a.premium - b.premium
      );
      const type = insurances.find((i) => {
        if (i.hidden || i.deleted || value > i.coverage) {
          return false;
        }
        if (!i.maxArea) {
          return true;
        }
        if (value === 0 || isNaN(value)) {
          if (area <= i.maxArea) {
            return true;
          } else {
            return false;
          }
        } else if (area <= i.maxArea && value <= i.coverage) {
          return true;
        }
      });
      if (!type) {
        return;
      }
      setAvailableInsurance(type);
      return type;
    },
    [insurance, unitData]
  );

  const insuranceTaxRate = useMemo(() => {
    if (!charges) {
      return 0;
    }
    return charges.reduce((acc, charge) => {
      if (charge.sChgCategory === "Insurance") {
        return acc + charge.dcTax1Rate + charge.dcTax2Rate;
      }
      return acc;
    }, 0);
  }, [charges]);

  useEffect(() => {
    const insurancePolicy = findInsurance(form.valueOfGoods);
    setLineItems((li) => {
      const item = li.find((i) => i.key === "insurance");
      if (insurancePolicy && item) {
        item.total = addVat(insurancePolicy.premium, insuranceTaxRate / 100);
        item.active = form.insurance === "yes";
      }
      return [...li];
    });
  }, [form.valueOfGoods, form.insurance, findInsurance, insuranceTaxRate]);

  const setCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    let newVal: boolean;
    value === "on" ? (newVal = false) : (newVal = true);
    updateForm((prevFormData) => ({ ...prevFormData, [name]: newVal }));
  };

  const setupLineItems = async () => {
    const avInsure = findInsurance();
    if (!selectedUnitType || !avInsure) {
      return;
    }
    setLineItems([]);
    const initialItems: LineItem[] = [
      {
        name: "First 4 Weeks Rent",
        key: "standard_rate",
        discounted: unitData?.unitType.rates.discounted,
        discountedRate: unitData?.unitType.rates.discounted
          ? unitData?.unitType.rates.discountedRate
          : 0.0,
        total: unitData?.unitType.rates.rate ?? 0.0,
        type: "rent",
        active: true,
        category: "move-in",
      },
      {
        name: "First 4 weeks StoreProtect",
        key: "insurance",
        total: addVat(avInsure.premium, insuranceTaxRate / 100),
        type: "insurance",
        active: true,
        category: "move-in",
      },
    ];
    if (costRetrieve && !fetchingCostRetrieve) {
      costRetrieve.costRetrieveFormatted.forEach((crt) => {
        switch (crt.ChargeDescription) {
          case "Refundable Security Deposit":
            initialItems.push({
              name: "Security Deposit",
              key: "security_deposit",
              total: crt.dcTotal,
              active: true,
              type: "required",
              category: "move-in",
            });
            break;

          case "First Monthly Rent Fee":
          case "First 4 Weeks Rent Fee":
            initialItems[0].discountedRate = crt.dcTotal;
            break;
          case "First Insurance Fee":
            initialItems[1].total = crt.dcTotal;
            break;

          default:
            initialItems.push({
              name: crt.ChargeDescription,
              key: crt.ChargeDescription,
              total: crt.dcTotal,
              active: true,
              type: "required",
              category: "move-in",
            });
            break;
        }
      });
    }
    setLineItems(initialItems);
  };

  const subTotal = useMemo(() => {
    let total = 0.0;
    for (const item of lineItems) {
      if (item.active) {
        if (item.discountedRate) {
          total += item.discountedRate;
        } else {
          total += item.total;
        }
      }
    }
    return formatCurrency(total);
  }, [lineItems]);

  const createLead = async () => {
    if (createLeadRetryCount >= CREATE_LEAD_RETRY_LIMIT) {
      Logger().error(`Create lead retry limit reached: ${createLeadRetryCount} attempts`);
      if (unitData) {
        const formattedSizes = formatSizes(
          unitData.descriptors.filter((d) => d._id !== unitData?.selectedDescriptor._id)
        );
        const unitSizes = getUnitSizes(formattedSizes.sortedList);
        const updatedDescriptors = unitData.descriptors.filter(
          (d) => d._id !== unitData?.selectedDescriptor._id
        );

        if (updatedDescriptors.length === 0) {
          Logger().error("No available unit types found after retry limit reached.");
          navigate(`/${location.slug}`);
          return;
        }

        store.dispatch(
          setUnitData({
            location: unitData.location,
            unitType: unitData.unitType,
            unitSizes: unitSizes,
            descriptors: updatedDescriptors,
            selectedDescriptor: updatedDescriptors[0],
            reservation: unitData.reservation,
          })
        );
      }
      navigate(`/${location.slug}`);
    }
    if (!reservationData || !skipFetchMoveInCostRetrieve) {
      return;
    }
    try {
      setCreateLeadRetryCount((prev) => prev + 1);
      const res = await makeReservation(reservationData).unwrap();
      store.dispatch(setReservationToken(res._id));
      setSkipMoveInCostRetrieve(false);
    } catch (err) {
      Logger().log(`Error creating reservation: ${JSON.stringify(err)}`);
    }
  };

  useEffect(() => {
    if (createReservationError) {
      Logger().error(
        `Reservation could not be created: ${JSON.stringify(createReservationError)}`
      );
    }
  }, [createReservationError]);

  const patchReservation = useCallback(async () => {
    const unitType = unitData?.unitType;
    if (!unitType) {
      return;
    }
    let neededDate = moment(reservationData?.payload.neededDate);
    if (neededDate.isBefore(moment())) {
      neededDate = moment().endOf("day");
    }

    const reservationPatch: ReservationPatchRequest = {
      token: reservationToken,
      locationCode: location.locationCode,
      payload: {
        tenant: {
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone,
          mobile: form.phone,
          countryCode: "0044",
          email: form.email,
          address: form.address_1,
          address2: form.address_2,
          city: form.town,
          state: form.county,
          postalCode: form.postcode,
          country: "United Kingdom",
        },
        reservation: {
          unitType: unitType._id,
          neededDate: neededDate.toISOString(),
        },
        cart: {},
        charges: {},
      },
    };
    /**
     * SETUP STATE FOR EXTRAS
     */
    if (charges && merchandise) {
      store.dispatch(
        setExtras({
          charges: charges,
          merchandise: merchandise,
        })
      );
    }
    try {
      await updateReservation(reservationPatch).unwrap();
      setSkipMoveInCostRetrieve(false);
    } catch (err) {
      setSubmissionState("partialComplete");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, location.locationCode, reservationToken, unitData, updateReservation]);

  const completeBooking = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValueOfGoodsError(null);
    const formElement = e.target as HTMLFormElement;
    setFormClass(`${classes.form} ${classes.was_validated}`);
    if (!formElement.checkValidity()) {
      // Focus on first invalid input
      const invalid = formElement.querySelectorAll(":invalid");
      if (invalid) {
        for (const item of invalid) {
          if (item?.getAttribute("name") === "valueOfGoods") {
            setValueOfGoodsError("Value of goods is invalid");
          }
          if (item?.getAttribute("name") === "moveInDate") {
            setNeededDateError(false);
          }
          if (item?.getAttribute("name") === "cardCVV") {
            setCardCCVError(false);
          }
          const input = item as HTMLInputElement;
          input.focus();
        }
        setSubmitting(false);
      }
      return;
    }
    setSubmitting(true);
    const resToken = reservationToken || reservationTokenParams;
    const reservationPatch: ReservationPatchRequest = {
      token: resToken,
      locationCode: location.locationCode,
      payload: {
        tenant: {
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone,
          mobile: form.phone,
          countryCode: "0044",
          email: form.email,
          address: form.address_1,
          address2: form.address_2,
          city: form.town,
          state: form.county,
          postalCode: form.postcode,
          country: "United Kingdom",
        },
        reservation: {
          deal: reservationData?.payload.deal,
          neededDate: form.moveInDate,
          insuranceCoverageId:
            form.insurance === "yes" ? availableInsurance?._id : undefined,
          insuranceDeclaredAmt: form.insurance === "yes" ? form.valueOfGoods : undefined,
          termsAndConditionsAgree: form.termsAndConditions,
          marketingAgree: form.allowMarketing,
          unitType: reservationData?.payload.unitType,
          insuranceType: form.insurance === "yes" ? "site" : "self",
        },
        cart: {},
        charges: {},
      },
    };
    lineItems.map((item: LineItem) => {
      if (item.active === true) {
        const itemId = item._id;
        if (!itemId) {
          return;
        }
        switch (item.type) {
          case "Boxes":
            reservationPatch.payload.cart[itemId] = 1;
            break;
          case "extra":
            if (item.key === "padlock_required") {
              reservationPatch.payload.cart[itemId] = 1;
            } else {
              reservationPatch.payload.charges[itemId] = 1;
            }
            break;
          default:
            break;
        }
      }
    });
    /**
     * SETUP STATE FOR EXTRAS
     */
    if (charges && merchandise) {
      store.dispatch(
        setExtras({
          charges: charges,
          merchandise: merchandise,
        })
      );
    }
    try {
      await updateReservation(reservationPatch).unwrap();
      setSubmissionState("prepareMoveIn");
    } catch (err) {
      Logger().error(`Patch reservation error: ${JSON.stringify(err)}`);
      setSubmissionState("error");
      setMoveInError(
        "There was an error updating your move-in details. Please try again."
      );
    }
  };

  const openPaymentWindow = (url: string) => {
    setPaymentLink(url);
    setShowPaymentModal(true);
  };

  const scaMoveInPayload = (reservationId: string): RapidStorScaMoveInRequest => {
    return {
      locationCode: location.locationCode,
      payload: {
        cardHoldersName: form.cardName,
        cardNumber: form.cardNumber,
        cardExpiryMonth: form.cardExpMonth,
        cardExpiryYear: form.cardExpYear,
        cardCVV: form.cardCVV,
        cardTypeId: parseInt(form.cardType),
        reservationId,
        view: "standalone",
        parentUri: window.location.href,
      },
    };
  };

  const handlePaymentResult = async (result: RapidStorPaymentResult) => {
    if (result.error === null) {
      return;
    }
    if (result.error) {
      if (submissionState !== "prepareMoveIn") {
        setMoveInError("An error has occured after processing your move in (payment)");
        setShowPaymentModal(false);
        setSubmissionState("partialComplete");
        return;
      }
      setMoveInError(
        "An error has occured while attempting to process your move in (payment)"
      );
      setShowPaymentModal(false);
      setSubmissionState("error");
      return;
    }
    if (result && !result.error) {
      setShowPaymentModal(false);
      switch (submissionState) {
        case "prepareMoveIn":
          setSubmissionState("complete");
          break;
        case "preparePayment":
          setSubmissionState("payment");
          break;
        default:
          Logger().error(
            `Unexpected submission state: ${submissionState} ${JSON.stringify(result)}`
          );
          setSubmissionState("error");
          setMoveInError("An unexpected error has occured. Please try again.");
      }
      return;
    }
  };

  const notifyMoveInComplete = () => {
    const rapidStorTrackEventPayload: RapidStorTrackEvent = {
      trackingId: createId(),
      url: window.location.href,
      referrerUrl: document.referrer,
      userAgent: window.navigator.userAgent,
      action: "move-in",
      timestamp: new Date().toISOString(),
    };
    const rapidStorTrackEvent: RapidStorTrackEventRequest = {
      locationToken: location.token,
      corpToken: options.corpToken,
      payload: rapidStorTrackEventPayload,
    };
    postTrackEvent(rapidStorTrackEvent);

    if (window.rapidstor_api_tracking_listener) {
      try {
        window.rapidstor_api_tracking_listener("move-in", {
          skip_account_creation: true,
          locationCode: location.locationCode,
          insurance_type: form.insurance,
          firstName: form.firstName,
          lastName: form.lastName,
        });
      } catch (err) {
        Logger().log(JSON.stringify(err));
      }
    }
  };

  useEffect(() => {
    Logger().log(`Submission state: ${submissionState}`);
    const usesSca = Object.keys(location.scaConfiguration).length > 0;
    const resToken = reservationToken ? reservationToken : reservationTokenParams;
    if (!resToken) {
      return;
    }

    const handleSubmitState = async () => {
      switch (submissionState) {
        case "init":
          return;
        case "prepareMoveIn":
          setSubmitting(true);
          if (!usesSca) {
            setSubmissionState("moveIn");
            return;
          }
          try {
            const scaMoveIn = await makeScaMoveIn(scaMoveInPayload(resToken)).unwrap();
            openPaymentWindow(scaMoveIn.redirect);
          } catch (err) {
            Logger().error(`Error preparing move-in: ${JSON.stringify(err)}`);
            setMoveInError(
              "There was an error processing your move-in. Please try again."
            );
            setSubmissionState("error");
          }
          break;
        case "moveIn":
          if (usesSca) {
            // setSubmissionState("prepareBillingInfoUpdate");
            // return;
          }
          try {
            const moveInPayload: RapidStorMoveInRequest = {
              locationCode: location.locationCode,
              token: resToken,
              payload: {
                autobill: {
                  enabled: form.autoBill,
                  usePaymentDetails: true,
                  type: parseInt(form.cardType),
                },
                card: {
                  type: parseInt(form.cardType),
                  number: form.cardNumber,
                  name: form.cardName,
                  cvv: form.cardCVV,
                  expiryMonth: form.cardExpMonth,
                  expiryYear: form.cardExpYear,
                },
              },
            };
            await makeMoveIn(moveInPayload).unwrap();
            setSubmissionState("complete");
          } catch (err) {
            setMoveInError(
              "There was an error processing your move-in. Please try again."
            );
            setSubmissionState("error");
          }
          return;
        case "preparePayment":
          // If there are no extras or boxes, skip payment
          if (
            !lineItems.some(
              (li) => li.active && (li.type === "extras" || li.type === "Boxes")
            )
          ) {
            setSubmissionState("complete");
            return;
          }
          try {
            const resp = await makeScaMoveInPayment(scaMoveInPayload(resToken)).unwrap();
            openPaymentWindow(resp.redirect);
          } catch (err) {
            Logger().error(`Error preparing payment: ${JSON.stringify(err)}`);
            if (cookies.user) {
              removeCookie("user", { url: "/" });
            }
            navigate(`/${location.slug}/confirmed-with-error/${reservationToken}`);
          }
          break;
        case "payment":
          setSubmissionState("complete");
          break;
        case "complete":
          setSubmitting(false);
          if (cookies.user) {
            removeCookie("user", { url: "/" });
          }
          notifyMoveInComplete();
          navigate(`/${location.slug}/confirmed/${reservationToken}`);
          break;
        case "partialComplete":
          setSubmitting(false);
          if (cookies.user) {
            removeCookie("user", { url: "/" });
          }
          navigate(`/${location.slug}/confirmed-with-error/${reservationToken}`);
          break;
        case "error":
          setSubmitting(false);
          break;
      }
    };
    handleSubmitState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionState]);

  const sqm = useMemo(() => {
    if (typeof unitData?.selectedDescriptor?.area !== "number") {
      return 0;
    }
    return (unitData?.selectedDescriptor?.area * 0.092903).toLocaleString("en-GB", {
      maximumFractionDigits: 1,
    });
  }, [unitData]);

  const update = (event: React.BaseSyntheticEvent) => {
    const { name, value } = event.target;
    updateForm((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const minMoveInDate = useMemo(() => {
    if (!location.rapidstor.siteOpeningDate) {
      return moment().endOf("day").toDate();
    }
    return moment
      .max([moment(), moment(location.rapidstor.siteOpeningDate, "DD-MM-YYYY")])
      .toDate();
  }, [location.rapidstor.siteOpeningDate]);

  return (
    <>
      {createReservationError && (
        <div>
          <h4>There was an error creating your reservation.</h4>
          <p>Please try again.</p>
          <button
            onClick={() => {
              history.back();
            }}
            className={classes.error_btn}
            style={{ marginRight: "8px" }}
          >
            Back
          </button>
          <button onClick={createLead} className={classes.error_btn}>
            Retry
            <IconRightChevron style={{ marginLeft: "8px" }} />
          </button>
          <div style={{ marginTop: "20px", fontSize: "small" }}>Technical details:</div>
          <code className={classes.error_details}>
            {JSON.stringify(createReservationError)}
          </code>
        </div>
      )}
      {!createReservationError && !loadingForm && <Spinner />}
      {!createReservationError && loadingForm && (
        <>
          <Row>
            <Col xs={12} sm={12} md={12}>
              <div className={classes.form_header}>
                <button
                  onClick={() => {
                    if (!createReservationLoading) {
                      history.back();
                    }
                  }}
                  className={classes.back_btn}
                >
                  <IconLeftChevron style={{ marginRight: "8px" }} />
                  Back
                </button>
                <BigHeader className={classes.bigHeader}>
                  {Constants.BOOKING_FORM.HEADER_TEXT}
                </BigHeader>
              </div>
              <Row className={classes.descriptor}>
                <Col xs={6} sm={6} md={{ order: 2, span: 6 }}>
                  <img width={"100%"} src={unitData?.selectedDescriptor.slides[0]} />
                </Col>
                <Col xs={6} sm={6} md={6}>
                  <div className={classes.descriptor_stats}>
                    <div>
                      <span className={classes.descriptor_area}>
                        {unitData?.selectedDescriptor.area}
                      </span>
                    </div>
                    <div className={classes.descriptor_small}>
                      <span>sqft</span>
                      <span>({sqm}m²)</span>
                    </div>
                  </div>
                  <div className={classes.discount_section}>
                    <h6>{unitData?.unitType.deal?.title}</h6>
                    <div className={classes.comparison}>
                      {reservationData?.payload.deal ? (
                        <span className={classes.old_rate}>
                          £{monthlyToWeekly(selectedUnitType?.rates.rate || 0)} per week
                        </span>
                      ) : null}
                      <span className={classes.discounted_rate}>
                        £{monthlyToWeekly(selectedUnitType?.rates.discountedRate || 0)}{" "}
                        per week
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
          <Form
            className={formClass}
            noValidate={true}
            onSubmit={(e) => completeBooking(e)}
          >
            <Row>
              <Col xs={12} sm={12} md={6}>
                <h4 className={classes.section_header}>
                  {Constants.BOOKING_FORM.SECTION_HEADERS.MOVE_IN}
                </h4>
                <span className={classes.inputErrorMsg} hidden={neededDateError}>
                  Maximum date selected cannot be more than 28 days in advance.
                </span>
                <ReactDatePicker
                  showIcon
                  selected={
                    form.moveInDate ? moment(form.moveInDate).toDate() : minMoveInDate
                  }
                  minDate={minMoveInDate}
                  maxDate={moment().add(28, "days").toDate()}
                  required
                  onFocus={(e) => {
                    e.currentTarget.blur();
                  }}
                  disabledKeyboardNavigation={true}
                  dateFormat={"dd/MM/yyyy"}
                  className={`${classes.datepicker}`}
                  onChange={(e) => {
                    if (e) {
                      updateForm((prevFormData) => ({
                        ...prevFormData,
                        ["moveInDate"]: new Date(e).toISOString(),
                      }));
                    }
                  }}
                />
              </Col>
            </Row>
            <Row>
              <h4 className={classes.section_header}>
                {Constants.BOOKING_FORM.SECTION_HEADERS.CONTACT_DETAILS}
              </h4>
              <Col xs={12} sm={12} md={6}>
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type={"text"}
                  required
                  name="firstName"
                  placeholder="First Name"
                  autoComplete="given-name"
                  value={form.firstName}
                  onChange={(e) => update(e)}
                />
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type={"text"}
                  required
                  name="lastName"
                  placeholder="Last Name"
                  autoComplete="family-name"
                  value={form.lastName}
                  onChange={(e) => update(e)}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={12} md={6}>
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type={"text"}
                  required
                  name="phone"
                  placeholder="Phone Number"
                  autoComplete="tel-national"
                  value={form.phone}
                  onChange={(e) => update(e)}
                />
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type={"email"}
                  required
                  name="email"
                  placeholder="Email Address"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update(e)}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={12} md={4}>
                <Form.Label>Address Line 1</Form.Label>
                <Form.Control
                  type={"text"}
                  name="address_1"
                  required
                  placeholder="Address Line 1"
                  autoComplete="address-line1"
                  value={form.address_1}
                  onChange={(e) => update(e)}
                />
              </Col>
              <Col xs={12} sm={12} md={4}>
                <Form.Label>Address Line 2</Form.Label>
                <Form.Control
                  type={"text"}
                  name="address_2"
                  placeholder="Address Line 2"
                  value={form.address_2}
                  onChange={(e) => update(e)}
                />
              </Col>
              <Col xs={12} sm={12} md={4}>
                <Form.Label>Town</Form.Label>
                <Form.Control
                  type={"text"}
                  required
                  name="town"
                  placeholder="Town"
                  autoComplete="address-level2"
                  value={form.town}
                  onChange={(e) => update(e)}
                />
              </Col>
              <Col xs={12} sm={12} md={4}>
                <Form.Label>County</Form.Label>
                <Form.Control
                  type={"text"}
                  name="county"
                  placeholder="County"
                  autoComplete="address-level1"
                  value={form.county}
                  onChange={(e) => update(e)}
                />
              </Col>
              <Col xs={12} sm={12} md={4}>
                <Form.Label>Postcode</Form.Label>
                <Form.Control
                  type={"text"}
                  required
                  name="postcode"
                  placeholder="Postcode"
                  autoComplete="postal-code"
                  value={form.postcode}
                  onChange={(e) => update(e)}
                />
              </Col>
            </Row>
            {/* INSURANCE COVERAGE */}
            <Row>
              <h4 className={classes.section_header}>
                {Constants.BOOKING_FORM.SECTION_HEADERS.INSURANCE_COVERAGE}
              </h4>
              <span style={{ fontSize: "14px", opacity: "0.6", color: "#000" }}>
                {Constants.BOOKING_FORM.INSURANCE_SUBTEXT.replace(
                  "{name}",
                  locationCompanyProfile()["name"]
                )}
              </span>
              <Col>
                <h6 style={{ fontSize: "14px", margin: "12px 0", opacity: "0.8" }}>
                  {Constants.BOOKING_FORM.SECTION_HEADERS.DECLARED_VALUE}
                </h6>
                {valueOfGoodsError && (
                  <span className={classes.inputErrorMsg}>{valueOfGoodsError}</span>
                )}
                <div className={classes.insuranceInputWrapper}>
                  <span className={classes.inputprefix}>£</span>
                  <Form.Control
                    type={"number"}
                    placeholder="0"
                    min={form.insurance === "yes" ? "1" : "0"}
                    name="valueOfGoods"
                    className={classes.numberinput}
                    required={true}
                    onChange={(e) => {
                      update(e);
                      if (
                        parseInt(e.target.value) > 1500 &&
                        form.insurance === "student"
                      ) {
                        setValueOfGoodsError("£1500 is the limit for Student Cover");
                        return;
                      } else {
                        setValueOfGoodsError(null);
                      }
                      const insurancePolicy = findInsurance(parseInt(e.target.value));
                      if (
                        !insurancePolicy ||
                        parseInt(e.target.value) > insurancePolicy?.coverage
                      ) {
                        if (form.insurance !== "student") {
                          setValueOfGoodsError(
                            "Value of goods exceeds maximum coverage amount"
                          );
                        }
                      } else {
                        setValueOfGoodsError(null);
                      }
                    }}
                    max={form.insurance === "student" ? "1500" : undefined}
                    onWheel={(e) => {
                      (e.target as HTMLInputElement).blur();
                    }}
                  />
                </div>
                <h6 style={{ fontSize: "14px", margin: "12px 0", opacity: "0.8" }}>
                  Select a cover option.
                </h6>
                <div className={classes.card}>
                  <div
                    className={classes.third}
                    onClick={() => {
                      updateForm({ ...form, insurance: "yes" });
                    }}
                  >
                    {!fetchingInsurance && (
                      <>
                        <div
                          style={{ display: "flex", marginBottom: "10px", flexGrow: "1" }}
                        >
                          <Form.Check
                            name="insurance"
                            checked={form.insurance === "yes"}
                            readOnly={true}
                          ></Form.Check>
                          <div className={classes.firstSection}>
                            <label>StoreProtect</label>
                            <span className={classes.desktop}>
                              Select this option if you would like to use OUR goods
                              protection.
                            </span>
                          </div>
                        </div>
                        <div className={`${classes.insurance} ${classes.mobile}`}>
                          <span className={classes.mobile}>
                            £
                            {formatCurrency(
                              addVat(
                                availableInsurance?.weeklyPremium,
                                insuranceTaxRate / 100
                              )
                            )}{" "}
                            per week
                          </span>
                          <span className={classes.mobile}>
                            for up to £{availableInsurance?.coverage} coverage
                          </span>
                          <span>
                            Select this option if you would like US to supply insurance
                          </span>
                        </div>
                        <div className={`${classes.pricing} ${classes.desktop}`}>
                          <span>
                            £
                            {formatCurrency(
                              addVat(
                                availableInsurance?.weeklyPremium,
                                insuranceTaxRate / 100
                              )
                            )}{" "}
                            per week
                          </span>
                          <span>
                            for up to £{formatCurrency(availableInsurance?.coverage)}{" "}
                            coverage
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div
                  className={classes.card}
                  onClick={() => {
                    updateForm({ ...form, insurance: "self" });
                  }}
                >
                  <div className={classes.third}>
                    <div style={{ display: "flex", marginBottom: "10px" }}>
                      <Form.Check
                        name="insurance"
                        checked={form.insurance === "self"}
                        readOnly={true}
                      ></Form.Check>
                      <div>
                        <label>
                          I have my own insurance and will provide a valid proof of policy
                        </label>
                        <span className={classes.subtext}>
                          {Constants.BOOKING_FORM.SELF_INSURANCE_SUBTEXT}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {unitData?.selectedDescriptor &&
                Number(unitData.selectedDescriptor.area) < 25 ? (
                  <div
                    className={classes.card}
                    onClick={() => {
                      updateForm({ ...form, insurance: "student" });
                    }}
                  >
                    <div className={classes.third}>
                      <div style={{ display: "flex", marginBottom: "10px" }}>
                        <Form.Check
                          name="insurance"
                          checked={form.insurance === "student"}
                          readOnly={true}
                        ></Form.Check>
                        <div>
                          <label>{Constants.BOOKING_FORM.STUDENT_INSURANCE}</label>
                          <span className={classes.subtext}>
                            {Constants.BOOKING_FORM.STUDENT_INSURANCE_SUBTEXT}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </Col>
            </Row>
            {/* BOOKING SUMMARY SECTION */}
            <Row>
              <Col xs={12} sm={12} md={6}>
                <h4 className={classes.section_header}>
                  {Constants.BOOKING_FORM.SECTION_HEADERS.SUMMARY}
                </h4>
                {["move-in"].map((category) => {
                  if (
                    !lineItems.find((item) => item.active && item.category === category)
                  ) {
                    return null;
                  }
                  return (
                    <div
                      className={classes.booking_summary}
                      key={`booking-summary-${category}`}
                    >
                      {lineItems.map((item, idx) => {
                        if (item.active === true && item.category === category) {
                          let discountedSection;
                          if (
                            item.discounted &&
                            item.key === "standard_rate" &&
                            unitData
                          ) {
                            discountedSection = (
                              <div className={classes.discounted_item}>
                                <span>(Deal: {unitData.unitType.deal?.title})</span>
                                <span>£{formatCurrency(item.discountedRate)}</span>
                              </div>
                            );
                          }
                          return (
                            <div
                              key={`line_item_idx_${idx}`}
                              className={classes.line_item}
                            >
                              <div className={classes.item}>
                                <span>{item.line_item_name ?? item.name}</span>
                                <span
                                  className={
                                    item.discounted && item.key === "standard_rate"
                                      ? classes.discounted
                                      : ""
                                  }
                                >
                                  £
                                  {item.discounted && item.key != "standard_rate"
                                    ? formatCurrency(item.discountedRate)
                                    : formatCurrency(item.total)}
                                </span>
                              </div>
                              {discountedSection}
                            </div>
                          );
                        }
                      })}
                    </div>
                  );
                })}
                <div className={classes.booking_summary}>
                  <div className={classes.total_item}>
                    <span>
                      <strong>Total to pay today</strong>
                    </span>
                    <span>
                      <strong>£{subTotal}</strong>
                    </span>
                  </div>
                </div>
                <span className={classes.tiny_text} style={{ marginTop: "3px" }}>
                  {Constants.BOOKING_FORM.SECURITY_DEPOSIT}
                </span>
              </Col>

              {/* PAYMENT DETAILS */}
              <Col xs={12} sm={12} md={6}>
                <h4 className={classes.section_header}>
                  {Constants.BOOKING_FORM.SECTION_HEADERS.PAYMENT_DETAILS}
                </h4>
                <Row>
                  <Col xs={12} sm={12} md={6}>
                    <Form.Label>Card Type</Form.Label>
                    <Form.Select
                      required
                      name="cardType"
                      value={form.cardType}
                      onChange={(e) => update(e)}
                    >
                      {Constants.CARD_TYPES.map((type) => {
                        return (
                          <option key={type.type} value={type.type}>
                            {type.name}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Col>
                  <Col xs={12} sm={12} md={12}>
                    <Form.Label>Cardholder&apos;s Name</Form.Label>
                    <Form.Control
                      type={"text"}
                      required
                      name="cardName"
                      autoComplete="cc-name"
                      placeholder="Cardholder's Name"
                      value={form.cardName}
                      onChange={(e) => update(e)}
                    />
                  </Col>
                  <Col xs={12} sm={12} md={12}>
                    <Form.Label>Card Number</Form.Label>
                    <Form.Control
                      type={"number"}
                      className={classes.numberinput}
                      required
                      name="cardNumber"
                      placeholder="Card Number"
                      autoComplete="cc-number"
                      value={undefined}
                      onChange={(e) => update(e)}
                    />
                  </Col>
                </Row>
                <span className={classes.inputErrorMsg} hidden={cardCVVError}>
                  AMEX Card Type is not supported
                </span>
                <Row>
                  <Col xs={6} sm={6} md={5}>
                    <Form.Label>Exp</Form.Label>
                    <div className={classes.formExpiry}>
                      <Form.Control
                        type={"text"}
                        required
                        name="cardExpMonth"
                        placeholder="MM"
                        autoComplete="cc-exp-month"
                        value={undefined}
                        onChange={(e) => {
                          update(e);
                        }}
                      />
                      <Form.Control
                        type={"text"}
                        required
                        name="cardExpYear"
                        placeholder="YY"
                        autoComplete="cc-exp-year"
                        value={undefined}
                        onChange={(e) => {
                          update(e);
                        }}
                      />
                    </div>
                  </Col>
                  <Col xs={6} sm={6} md={4}>
                    <Form.Label>CVV</Form.Label>
                    <Form.Control
                      type={"number"}
                      className={classes.numberinput}
                      required
                      max={999}
                      name="cardCVV"
                      autoComplete="cc-csc"
                      value={undefined}
                      onChange={(e) => update(e)}
                    />
                  </Col>
                </Row>
              </Col>
              {/* PAYMENT MODAL */}
              <Modal show={showPaymentModal} centered>
                <Modal.Body className="">
                  <PaymentModal
                    paymentLink={paymentLink}
                    onPaymentResult={handlePaymentResult}
                  />
                </Modal.Body>
              </Modal>
            </Row>
            <hr />
            {/* TERMS */}
            <Row style={{ marginBottom: "40px" }}>
              <Col xs={12} sm={12} md={7}>
                <h6 style={{ opacity: "0.6", fontSize: "0.8rem" }}>
                  Please view our{" "}
                  <a
                    className={classes.a}
                    href="/terms-conditions"
                    target="_blank"
                    rel="noreferrer"
                  >
                    terms and conditions
                  </a>
                  {" and "}
                  <a
                    className={classes.a}
                    href="/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    privacy policy
                  </a>{" "}
                  on the {locationCompanyProfile().name} website
                </h6>
                <div className={classes.check_section}>
                  <Form.Check
                    name={"termsAndConditions"}
                    id="termsAndConditions"
                    value={form.termsAndConditions ? "on" : "off"}
                    required
                    onChange={(e) => setCheckbox(e)}
                  ></Form.Check>
                  <label htmlFor="termsAndConditions">
                    {Constants.BOOKING_FORM.TERMS_AND_CONDITIONS_WAIVER}
                  </label>
                </div>
                <div className={classes.check_section}>
                  <Form.Check
                    name={"allowMarketing"}
                    id="allowMarketing"
                    value={form.allowMarketing ? "on" : "off"}
                    checked={form.allowMarketing}
                    onChange={(e) => setCheckbox(e)}
                  ></Form.Check>
                  <label htmlFor="allowMarketing">
                    {Constants.BOOKING_FORM.ALLOW_MARKETING}
                  </label>
                </div>
              </Col>
              <Col xs={12} sm={12} md={5}>
                <div className={classes.final_section}>
                  <div className={classes.subtotal}>
                    <span className={classes.value}>£{subTotal}</span>
                    <span> {Constants.BOOKING_FORM.DUE}</span>
                  </div>
                  {moveInError && (
                    <div className={classes.moveInError}>
                      <h4>ERROR: {moveInError}</h4>
                    </div>
                  )}
                  <PrimaryButton
                    submit
                    responsive
                    disabled={submitting}
                    loading={submitting}
                  >
                    {submitting
                      ? Constants.BOOKING_FORM.BUTTON_TEXT_SUBMITTING
                      : Constants.BOOKING_FORM.BUTTON_TEXT}
                  </PrimaryButton>
                </div>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </>
  );
}

export default BookConfirm;
